import { useRef, useEffect } from "react";
import type { SurfaceMarker } from "../../types/game";

interface Props {
  planetId: string;
  markers?: SurfaceMarker[];
}

const MARKER_COLORS: Record<SurfaceMarker["type"], string> = {
  away_team: "#00ccff",
  biosign: "#44ff44",
  poi: "#ffcc00",
  hazard: "#ff4444",
};

const MARKER_SYMBOLS: Record<SurfaceMarker["type"], string> = {
  away_team: "\u25B2",  // triangle up
  biosign: "\u25CF",    // filled circle
  poi: "\u2605",        // star
  hazard: "\u26A0",     // warning
};

export function SurfaceMap({ planetId, markers = [] }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const w = canvas.width = canvas.offsetWidth * 2;
    const h = canvas.height = canvas.offsetHeight * 2;
    ctx.scale(2, 2);
    const dw = w / 2;
    const dh = h / 2;

    // Background
    ctx.fillStyle = "#0a0e14";
    ctx.fillRect(0, 0, dw, dh);

    // Grid lines (lat/lon)
    ctx.strokeStyle = "rgba(0, 180, 255, 0.12)";
    ctx.lineWidth = 1;
    for (let i = 1; i < 6; i++) {
      const y = (dh / 6) * i;
      ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(dw, y); ctx.stroke();
    }
    for (let i = 1; i < 8; i++) {
      const x = (dw / 8) * i;
      ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, dh); ctx.stroke();
    }

    // Terrain contours (procedural)
    ctx.strokeStyle = "rgba(0, 180, 255, 0.08)";
    ctx.lineWidth = 1;
    const seed = hashString(planetId);
    for (let c = 0; c < 5; c++) {
      ctx.beginPath();
      const baseY = dh * 0.2 + ((seed * (c + 1) * 7) % 100) / 100 * dh * 0.6;
      for (let x = 0; x <= dw; x += 4) {
        const y = baseY + Math.sin(x * 0.02 + seed * c) * 20 + Math.sin(x * 0.007 + c) * 40;
        x === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
      }
      ctx.stroke();
    }

    // Markers
    for (const marker of markers) {
      const x = ((marker.lon + 180) / 360) * dw;
      const y = ((90 - marker.lat) / 180) * dh;
      const color = MARKER_COLORS[marker.type];
      const symbol = MARKER_SYMBOLS[marker.type];

      // Pulse ring
      ctx.beginPath();
      ctx.arc(x, y, 12, 0, Math.PI * 2);
      ctx.strokeStyle = color;
      ctx.lineWidth = 1;
      ctx.stroke();

      // Symbol
      ctx.fillStyle = color;
      ctx.font = "bold 14px monospace";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(symbol, x, y);

      // Label
      ctx.font = "10px monospace";
      ctx.fillText(marker.label, x, y + 20);
    }

    // Title
    ctx.fillStyle = "rgba(0, 180, 255, 0.6)";
    ctx.font = "11px monospace";
    ctx.textAlign = "left";
    ctx.textBaseline = "top";
    ctx.fillText(`SURFACE: ${formatId(planetId)}`, 12, 10);

    // Legend
    const legendY = dh - 20;
    let legendX = 12;
    for (const type of Object.keys(MARKER_COLORS) as SurfaceMarker["type"][]) {
      ctx.fillStyle = MARKER_COLORS[type];
      ctx.fillText(`${MARKER_SYMBOLS[type]} ${type.replace(/_/g, " ")}`, legendX, legendY);
      legendX += 100;
    }
  }, [planetId, markers]);

  return (
    <div className="surface-map">
      <canvas ref={canvasRef} className="surface-map-canvas" />
    </div>
  );
}

function formatId(id: string): string {
  return id.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
}

function hashString(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = ((h << 5) - h + s.charCodeAt(i)) | 0;
  return Math.abs(h);
}
